/**
 * ThreadPicker Component
 *
 * Popover menu for selecting a thread from a grouped badge.
 * Shows a scrollable list of threads with author, preview, and timestamp.
 *
 * Features:
 * - Header with title and count badge
 * - Scrollable thread list (max-height: 240px)
 * - Keyboard navigation (arrows, enter, escape)
 * - Fade-in animation
 */

import React, { useState, useRef, useEffect } from 'react';
import { ThreadRow } from './ThreadRow';
import type { Comment, TeamMember } from '../../lib/types';
import { tokens } from '../../styles/tokens';

export interface ThreadPickerProps {
  /** Threads to display in the picker */
  threads: Comment[];
  /** Map of author ID to team member info */
  authors: Map<string, TeamMember>;
  /** Map of comment ID to reply count */
  replyCounts: Map<string, number>;
  /** Callback when a thread is selected */
  onSelect: (comment: Comment) => void;
  /** Callback to close the picker */
  onClose: () => void;
  /** Floating-ui ref setter for the picker element */
  floatingRef: (node: HTMLElement | null) => void;
  /** Floating-ui computed styles */
  floatingStyles: React.CSSProperties;
  /** Whether floating-ui has finished calculating position */
  isPositioned: boolean;
  /** Set of unread comment IDs */
  unreadCommentIds?: Set<string>;
}

export const ThreadPicker: React.FC<ThreadPickerProps> = ({
  threads,
  authors,
  replyCounts,
  onSelect,
  onClose,
  floatingRef,
  floatingStyles,
  isPositioned,
  unreadCommentIds = new Set(),
}) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Check if floating-ui has actually calculated position (has transform)
  // isPositioned can be true before transform is added to floatingStyles
  const hasTransform = 'transform' in floatingStyles;
  const isFullyPositioned = isPositioned && hasTransform;

  // Wait one frame after transform is calculated before making visible
  // This ensures the browser has painted the transform before we show the element
  const [isVisiblyPositioned, setIsVisiblyPositioned] = useState(false);

  useEffect(() => {
    // If we're not fully positioned, immediately hide again
    if (!isFullyPositioned) {
      setIsVisiblyPositioned(false);
      return;
    }

    // Wait one animation frame after the transform is committed
    const rafId = requestAnimationFrame(() => {
      setIsVisiblyPositioned(true);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isFullyPositioned]);

  // Focus first item on mount
  useEffect(() => {
    const firstItem = listRef.current?.querySelector('[data-thread-row]') as HTMLElement;
    firstItem?.focus();
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(prev + 1, threads.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (threads[focusedIndex]) {
          onSelect(threads[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  // Focus the item when focusedIndex changes
  useEffect(() => {
    const items = listRef.current?.querySelectorAll('[data-thread-row]');
    if (items && items[focusedIndex]) {
      (items[focusedIndex] as HTMLElement).focus();
    }
  }, [focusedIndex]);

  const getAuthor = (authorId: string): TeamMember => {
    return authors.get(authorId) ?? {
      id: authorId,
      name: 'Unknown',
      email: '',
      avatar: null,
    };
  };

  return (
    <div
      ref={floatingRef}
      data-testid="thread-picker"
      data-multidev-sdk="true"
      role="listbox"
      aria-label="Select a thread"
      onKeyDown={handleKeyDown}
      style={{
        ...floatingStyles,
        width: '288px',
        backgroundColor: tokens.colors.bgDefault,
        border: `1px solid ${tokens.colors.borderDefault}`,
        borderRadius: tokens.borders.radiusContainer,
        boxShadow: tokens.elevation.overlay,
        zIndex: 999999,
        // Hide until browser has painted the transform (one frame after calculation)
        visibility: isVisiblyPositioned ? 'visible' : 'hidden',
        opacity: isVisiblyPositioned ? 1 : 0,
        pointerEvents: isVisiblyPositioned ? 'auto' : 'none',
        transition: 'opacity 150ms ease-out',
        willChange: 'transform, opacity',
        // Explicitly disable animation to avoid conflicts
        animation: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
          borderBottom: `1px solid ${tokens.colors.borderDefault}`,
          backgroundColor: tokens.colors.bgSecondary,
        }}
      >
        <span
          style={{
            fontSize: '14px',
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.textDefault,
          }}
        >
          Select a thread
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px',
            height: '24px',
            padding: `0 ${tokens.spacing['2xs']}`,
            backgroundColor: tokens.colors.primary,
            color: tokens.colors.textReverse,
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: tokens.typography.fontWeight.medium,
          }}
        >
          {threads.length}
        </span>
      </div>

      {/* Thread list */}
      <div
        ref={listRef}
        style={{
          maxHeight: '240px',
          overflowY: 'auto',
        }}
      >
        {threads.map((thread, index) => (
          <ThreadRow
            key={thread.id}
            thread={thread}
            author={getAuthor(thread.authorId)}
            replyCount={replyCounts.get(thread.id) ?? 0}
            onClick={() => onSelect(thread)}
            isFocused={index === focusedIndex}
            onFocus={() => setFocusedIndex(index)}
            isUnread={unreadCommentIds.has(thread.id)}
          />
        ))}
      </div>
    </div>
  );
};
